import type { CardControls, Merchant, NetworkConfig, World } from './types'

/**
 * Every value here is invented. The PAN sits in a range reserved for
 * documentation and fails a Luhn check on purpose, so it can never be mistaken
 * for a live credential. The merchants do not exist.
 */

const defaultControls: CardControls = {
  perTransactionLimitMinor: 500_000, // R5 000.00
  dailyLimitMinor: 1_000_000, // R10 000.00
  blockedMccs: [],
  allowedMccs: [],
  channels: {
    'card-present': true,
    'card-not-present': true,
    contactless: true,
    atm: true,
  },
  allowedCountries: [],
}

export const MERCHANTS: Merchant[] = [
  {
    id: 'm_roastery',
    name: 'Kloof Street Roastery',
    mcc: '5812',
    country: 'ZA',
    enrolledInAccountUpdater: false,
  },
  {
    id: 'm_fuel',
    name: 'Sandveld Fuel',
    mcc: '5541',
    country: 'ZA',
    enrolledInAccountUpdater: false,
  },
  {
    id: 'm_streaming',
    name: 'Lumo Streaming',
    mcc: '5815',
    country: 'ZA',
    enrolledInAccountUpdater: true,
  },
]

/**
 * Stand-in and risk configuration. These are the issuer's own settings, even
 * though Visa is what enforces them. That is the point people miss: Visa is
 * not making up its own rules, it is applying the issuer's.
 */
export const defaultConfig: NetworkConfig = {
  issuerReachable: true,
  stip: {
    enabled: true,
    limitMinor: 200_000, // R2 000.00
  },
  vrmRules: [
    {
      id: 'vrm_cnp_ceiling',
      label: 'Decline online payments above R1 500',
      description:
        'An issuer-configured Visa Risk Manager rule. It runs at VisaNet, so the issuer host never sees the transaction at all.',
      enabled: false,
      thresholdMinor: 150_000,
      channel: 'card-not-present',
      responseCode: '05',
    },
    {
      id: 'vrm_block',
      label: 'Block this card at VisaNet',
      description:
        'A hard block held on the Visa exception file. Returns a category 1 decline, which tells the merchant never to retry.',
      enabled: false,
      responseCode: '62',
    },
  ],
  // Illustrative only. South African interchange is regulated and published by
  // the SARB, and real rates vary by card type, channel and merchant category.
  fees: {
    interchangeBps: 120,
    schemeFeeBps: 10,
    merchantServiceChargeBps: 195,
  },
}

export function initialWorld(): World {
  return {
    day: 0,
    seq: 0,
    account: {
      id: 'acc_1',
      name: 'Everyday cheque account',
      currency: 'ZAR',
      ledgerMinor: 1_200_000, // R12 000.00
      status: 'open',
    },
    cards: [
      {
        id: 'card_1',
        accountId: 'acc_1',
        pan: '4999990000000012',
        expiry: { month: 11, year: 2029 },
        state: 'active',
        formFactor: 'physical',
        controls: defaultControls,
        par: 'V0010013820200000000000000123',
      },
    ],
    tokens: [],
    transactions: [],
    settlements: [],
    merchants: MERCHANTS,
    config: structuredClone(defaultConfig),
  }
}
